import React from 'react';
import styled from 'styled-components';
import type { UIMessage } from 'ai';

import { Box, Flex, Icons, Text, Theme } from 'baukasten';

import type { ActionSpec } from '../types';
import {
  actionSuggestions,
  answerText,
  attachments,
  fileNames,
  fixHeadingGlue,
  thinkingSteps,
} from '../utils/messageParts';
import { ActionSuggestions } from './ActionSuggestions';
import { ChatMarkdown } from './ChatMarkdown';
import { CopyAnswerButton } from './CopyAnswerButton';
import { DownloadChip } from './DownloadChip';
import { ThinkingBlock } from './ThinkingBlock';

const UserBubble = styled(Box)`
  max-width: 85%;
  border-radius: 12px;
  background-color: ${Theme.getColor('grey-light-50')};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

type Props = {
  message: UIMessage;
  isLast: boolean;
  turnStartedAt: number | undefined;
  streaming: boolean;
  actionsEnabled: boolean;
  chosenAction: string | null;
  onAction: (action: ActionSpec) => void;
};

export const ChatMessage = ({
  message,
  isLast,
  turnStartedAt,
  streaming,
  actionsEnabled,
  chosenAction,
  onAction,
}: Props) => {
  const text = answerText(message);

  if (message.role === 'user') {
    return (
      <Flex col alignItems="flex-end" mb={4} data-message-id={message.id}>
        {attachments(message).map(file => (
          <Flex key={file.url} alignItems="center" gap={1} mb={2}>
            <Icons.Attachment color="grey-dark" size="16px" />
            <Text fontSize={14}>{fileNames(file).filename}</Text>
          </Flex>
        ))}
        {text && (
          <UserBubble px={3} py={2}>
            <Text fontSize={14}>{text}</Text>
          </UserBubble>
        )}
      </Flex>
    );
  }

  const steps = thinkingSteps(message);
  const files = attachments(message);
  const actions = actionSuggestions(message);
  const streamingThisMessage = streaming && isLast;

  return (
    <Box mb={4} data-message-id={message.id}>
      {/* narration can't be told from the answer mid-stream, so the turn thinks until it ends */}
      <ThinkingBlock
        steps={steps}
        active={streamingThisMessage}
        turnStartedAt={turnStartedAt}
      />
      {files.map(file => (
        <DownloadChip key={file.url} file={file} />
      ))}
      {text && <ChatMarkdown text={fixHeadingGlue(text)} />}
      {text && !streamingThisMessage && (
        <Flex gap={1} alignItems="center" flexWrap="wrap" mt={1}>
          <CopyAnswerButton text={text} />
          <ActionSuggestions
            actions={actions}
            active={isLast && actionsEnabled}
            chosen={chosenAction}
            onAction={onAction}
          />
        </Flex>
      )}
    </Box>
  );
};
